import React, { useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import australia from 'stile-shared/assets/images/homepage/australia.svg';
import footerBackground from 'stile-shared/assets/images/homepage/homepage_footer_background.svg';
import footerBackgroundMobile from 'stile-shared/assets/images/homepage/homepage_footer_background_mobile.png';

import { Button } from 'stile-shared/src/components/2024/common/Button';

const FooterMap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: end;
  position: relative;
  overflow: hidden;
  height: 100%;
  margin-top: 100px;
  padding: 100px 0 150px;
  &::before {
    content: '';
    position: absolute;
    bottom: -99%;
    left: 50%;
    transform: translateX(-50%);
    width: 144%;
    height: 196%;
    border-radius: 50%;
    background-color: var(--stile-green-2);
    background-image: url(${footerBackground});
    background-size: fit;
    background-position: right top;
    background-repeat: repeat;
    border-top-left-radius: 100%;
    border-top-right-radius: 100%;
    z-index: -1;
    max-width: 2650px;
    min-width: 2116px;
  }
  @media (max-width: 768px) {
    padding: 110px 25px 80px;
    &::before {
      margin-top: 60px;
      height: 150%;
      min-width: 1100px;
      bottom: -50%;
      background-position: center top;
      background-size: fit;
      background-image: url(${footerBackgroundMobile});
    }
  }

  .scroll-section {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .wrapper {
    max-width: 860px;
    width: 100%;
    text-align: center;
    color: var(--white);
    transition: transform 0.3s ease-out;
    @media (max-width: 1160px) {
      margin: 0 60px;
    }
    @media (max-width: 768px) {
      position: relative;
    }
  }

  h2 {
    margin: 40px 0;
    @media (max-width: 768px) {
      margin-bottom: 0 0 24px;
    }
  }

  .australia-map {
    width: 655px;
    height: auto;
    transform-origin: center center;
    transition: transform 0.1s linear, margin-top 0.3s ease-out;
    @media (max-width: 768px) {
      width: 300px;
    }
  }
`;

export default function ScrollAnimation() {
  const containerRef = useRef<HTMLDivElement>(null);
  // 'progress' drives the map animation; it goes from 0 to 1 when the component is fully in view.
  const [progress, setProgress] = useState(0);
  // 'headerProgress' drives the heading animation; it starts increasing earlier.
  const [headerProgress, setHeaderProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    function handleScroll() {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      // 'start' is when the top of the viewport touches the component’s bottom.
      const start = window.innerHeight;
      // 'end' is when the component is fully in view (its entire height is visible).
      const end = window.innerHeight - rect.height;

      // For the map, we calculate progress so that once the component is fully in view, progress === 1.
      const mapProg = (start - rect.top) / (start - end); // equals (window.innerHeight - rect.top) / rect.height
      setProgress(Math.min(Math.max(mapProg, 0), 1));

      // For the heading, we add an offset so it starts moving down earlier.
      const headerOffset = 100; // adjust this value to start earlier
      const headerProg = (start - rect.top + headerOffset) / (start - end + headerOffset);
      setHeaderProgress(Math.min(Math.max(headerProg, 0), 1));
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // For the map:
  //  - scale from ~0.809 (initial ~530px wide) to 1.0 (final 655px wide)
  //  - margin-top from 320px down to 80px
  const initialScale = 0.809; // approx 530px if base width is 655px
  const finalScale = 1.0;
  const scaleRange = finalScale - initialScale;

  const initialMargin = 320;
  const finalMargin = 80;
  const marginRange = initialMargin - finalMargin;

  const mapScale = isMobile ? 1 : initialScale + scaleRange * progress;
  const mapMarginTop = isMobile ? 30 : initialMargin - marginRange * progress; // 320 -> 80

  return (
    <FooterMap>
      <div ref={containerRef} className="scroll-section">
        <div
          className="wrapper"
          style={{
            transform: `translateY(${isMobile ? 0 : headerProgress * 50}px)`,
          }}
        >
          <h2>Over 1 in 3 Australian schools are part of the Stile community</h2>
          <Button
            label="See Stile in action at a nearby school"
            style="grey"
            href="/set-up-trial/"
            newTab={false}
          />
        </div>

        <img
          className="australia-map"
          src={australia}
          alt="Australia"
          style={{
            transform: `scale(${mapScale})`,
            marginTop: `${mapMarginTop}px`,
          }}
        />
      </div>
    </FooterMap>
  );
}
